import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#22C55E', blue:'#FFFFFF',
 red:'#EF4444', redBg:'rgba(239,68,68,.10)', redBorder:'rgba(239,68,68,.25)',
 amber:'#F59E0B', amberBg:'rgba(245,158,11,.10)', amberBorder:'rgba(245,158,11,.25)',
 green:'#22C55E', greenBg:'rgba(34,197,94,.10)', greenBorder:'rgba(34,197,94,.25)',
 purple:'#A78BFA',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});

const TASK_TYPES = ['Cleaning','Deep Cleaning','Turndown','Inspection','Linen Change','Bathroom Clean','Minibar Restock','Maintenance Report','Lost & Found','Guest Request'];
const PRIORITIES = ['High','Normal','Low'];
const STATUSES = ['Pending','In Progress','Completed','Skipped'];

const STATUS_C = {
 'Pending': { color:C.amber, bg:C.amberBg, border:C.amberBorder },
 'In Progress': { color:'#60A0E0', bg:'rgba(96,160,224,.1)', border:'rgba(96,160,224,.25)' },
 'Completed': { color:C.green, bg:C.greenBg, border:C.greenBorder },
 'Skipped': { color:C.text3, bg:'rgba(96,104,128,.1)', border:'rgba(96,104,128,.25)' },
};

const PRIORITY_C = {
 'High': C.red,
 'Normal': C.amber,
 'Low': C.green,
};

const ROOM_STATUS = {
 'Vacant Clean': { color:C.green, },
 'Vacant Dirty': { color:C.amber, },
 'Occupied': { color:'#60A0E0', },
 'Maintenance': { color:C.red, },
 'Blocked': { color:C.text3, },
};

const inp = { width:'100%', padding:'10px 12px', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:14, boxSizing:'border-box', outline:'none', fontFamily:'inherit' };
const sel = { ...inp, cursor:'pointer' };

export default function Housekeeping() {
 const [tasks, setTasks] = useState([]);
 const [rooms, setRooms] = useState([]);
 const [roomTypes, setRoomTypes] = useState([]);
 const [users, setUsers] = useState([]);
 const [stats, setStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [success, setSuccess] = useState('');
 const [tab, setTab] = useState('tasks');
 const [showAdd, setShowAdd] = useState(false);
 const [filterStatus, setFilterStatus] = useState('All');
 const [filterPriority, setFilterPriority] = useState('All');

 const [form, setForm] = useState({ room_id:'', task_type:'Cleaning', assigned_to:'', priority:'Normal', notes:'' });
 const [saving, setSaving] = useState(false);

 const load = useCallback(async () => {
 setLoading(true); setError('');
 try {
 const [t, r, rt, u, s] = await Promise.all([
 fetch(`${API}/housekeeping/all`).then(x=>x.json()),
 fetch(`${API}/rooms`).then(x=>x.json()),
 fetch(`${API}/rooms/types`).then(x=>x.json()),
 fetch(`${API}/sessions/users`).then(x=>x.json()),
 fetch(`${API}/housekeeping/stats`).then(x=>x.json()),
 ]);
 setTasks(Array.isArray(t)?t:[]);
 setRooms(Array.isArray(r)?r:[]);
 setRoomTypes(Array.isArray(rt)?rt:[]);
 setUsers(Array.isArray(u)?u.filter(u=>u.role==='Housekeeping'||u.role_id===5):[]);
 setStats(s);
 } catch(e) { setError(e.message); }
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 const showMsg = (msg, isErr=false) => {
 if(isErr) setError(msg); else setSuccess(msg);
 setTimeout(()=>{ setError(''); setSuccess(''); }, 3000);
 };

 const handleCreate = async () => {
 if (!form.task_type) { showMsg('Select task type', true); return; }
 setSaving(true);
 try {
 const r = await fetch(`${API}/housekeeping`, {
 method:'POST', headers:{'Content-Type':'application/json'},
 body: JSON.stringify(form),
 });
 const d = await r.json();
 if (!r.ok) throw new Error(d.error||'Failed');
 showMsg('Task created!');
 setShowAdd(false);
 setForm({ room_id:'', task_type:'Cleaning', assigned_to:'', priority:'Normal', notes:'' });
 load();
 } catch(e) { showMsg(e.message, true); }
 finally { setSaving(false); }
 };

 const handleUpdateStatus = async (id, status) => {
 try {
 const r = await fetch(`${API}/housekeeping/${id}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ status }),
 });
 if (!r.ok) throw new Error('Failed');
 setTasks(p=>p.map(t=>t.id===id?{...t,status}:t));
 if (status==='Completed') showMsg('Task completed! ');
 } catch(e) { showMsg(e.message, true); }
 };

 const handleUpdateRoom = async (roomId, status) => {
 try {
 const r = await fetch(`${API}/rooms/${roomId}/status`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ status }),
 });
 if (!r.ok) {
 await fetch(`${API}/rooms/${roomId}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ status }),
 });
 }
 setRooms(p=>p.map(r=>r.id===roomId?{...r,status}:r));
 showMsg(`Room status updated to ${status}`);
 } catch(e) { showMsg(e.message, true); }
 };

 // Filter tasks
 let filtered = tasks;
 if (filterStatus!=='All') filtered = filtered.filter(t=>t.status===filterStatus);
 if (filterPriority!=='All') filtered = filtered.filter(t=>t.priority===filterPriority);

 const roomStats = stats?.rooms || {};
 const taskStats = stats?.tasks || {};

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>

 {/* Header */}
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
 <div>
 <div style={{ fontSize:22, fontWeight:800 }}> Housekeeping</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>Room status · Task management · Live tracking</div>
 </div>
 <div style={{ display:'flex', gap:10 }}>
 <button onClick={load} style={{ padding:'9px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}> Refresh</button>
 <button onClick={()=>setShowAdd(true)} style={{ padding:'9px 18px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>+ Add Task</button>
 </div>
 </div>

 {error && <div style={{ padding:'10px 14px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, color:C.red, fontSize:13, marginBottom:16 }}> {error}</div>}
 {success && <div style={{ padding:'10px 14px', background:C.greenBg,border:`1px solid ${C.greenBorder}`,borderRadius:8, color:C.green, fontSize:13, marginBottom:16 }}> {success}</div>}

 {/* Stats */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
 {[
 {label:'Vacant Clean', value:roomStats.vacant_clean||0, color:C.green, },
 {label:'Occupied', value:roomStats.occupied||0, color:'#60A0E0', },
 {label:'Dirty', value:roomStats.vacant_dirty||0, color:C.amber, },
 {label:'Maintenance', value:roomStats.maintenance||0, color:C.red, },
 {label:'Tasks Pending', value:taskStats.pending||0, color:C.amber, },
 {label:'In Progress', value:taskStats.in_progress||0, color:'#60A0E0', },
 {label:'Completed Today',value:taskStats.completed||0, color:C.green, },
 ].map(s=>(
 <div key={s.label} style={{ background:C.surface, border:`1px solid ${s.color}33`, borderRadius:12, padding:'14px 16px' }}>
 <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
 <div style={{ fontSize:24, fontWeight:900, color:s.color }}>{s.value}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{s.label}</div>
 </div>
 ))}
 </div>

 {/* Tabs */}
 <div style={{ display:'flex', gap:4, marginBottom:20, background:C.surface, borderRadius:10, padding:4, width:'fit-content' }}>
 {[
 {id:'tasks', label:' Tasks'},
 {id:'rooms', label:' Room Status'},
 {id:'schedule', label:' Schedule'},
 ].map(t=>(
 <button key={t.id} onClick={()=>setTab(t.id)}
 style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background:tab===t.id?C.accent:'transparent', color:tab===t.id?'#fff':C.text2 }}>
 {t.label}
 </button>
 ))}
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading…</div>}

 {/* ── TASKS TAB ── */}
 {!loading && tab==='tasks' && (
 <div>
 {/* Filters */}
 <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
 <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...sel, width:160 }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="All">All Status</option>
 {STATUSES.map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <select value={filterPriority} onChange={e=>setFilterPriority(e.target.value)} style={{ ...sel, width:140 }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="All">All Priority</option>
 {PRIORITIES.map(p=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={p}>{p}</option>)}
 </select>
 </div>

 {filtered.length===0 ? (
 <div style={{ textAlign:'center', padding:60, background:C.surface, borderRadius:14, border:`1px solid ${C.border}` }}>
 <div style={{ fontSize:40, marginBottom:12 }}></div>
 <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:6 }}>No tasks found</div>
 <div style={{ fontSize:13, color:C.text2 }}>All rooms are clean or no tasks match filter</div>
 <button onClick={()=>setShowAdd(true)} style={{ marginTop:16, padding:'10px 20px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>+ Create First Task</button>
 </div>
 ) : (
 <div style={{ display:'grid', gap:10 }}>
 {filtered.map(task => {
 const st = STATUS_C[task.status] || STATUS_C['Pending'];
 const pri = PRIORITY_C[task.priority] || C.text3;
 return (
 <div key={task.id} style={{ background:C.surface, border:`1px solid ${st.border}`, borderRadius:12, padding:16 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
 <div>
 <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text }}>{task.task_type}</div>
 <span style={{ padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:800, background:`${pri}20`, color:pri }}>{task.priority}</span>
 <span style={{ padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:800, background:st.bg, color:st.color }}>{task.status}</span>
 </div>
 <div style={{ fontSize:12, color:C.text2 }}>
 {task.room_number ? `Room #${task.room_number}` : 'General Task'}
 {task.room_type ? ` · ${task.room_type}` : ''}
 {task.assigned_to_name ? ` · ${task.assigned_to_name}` : ' · Unassigned'}
 </div>
 {task.completed_time && (
 <div style={{ fontSize:11, color:C.green, marginTop:2 }}>
 Completed: {new Date(task.completed_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
 </div>
 )}
 </div>
 {/* Status actions */}
 <div style={{ display:'flex', gap:6, flexShrink:0, marginLeft:12 }}>
 {task.status === 'Pending' && (
 <button onClick={()=>handleUpdateStatus(task.id,'In Progress')}
 style={{ padding:'6px 12px', background:'rgba(96,160,224,.2)', border:'1px solid rgba(96,160,224,.4)', borderRadius:6, color:'#60A0E0', cursor:'pointer', fontSize:11, fontWeight:600 }}>
 Start
 </button>
 )}
 {task.status === 'In Progress' && (
 <button onClick={()=>handleUpdateStatus(task.id,'Completed')}
 style={{ padding:'6px 12px', background:C.greenBg, border:`1px solid ${C.greenBorder}`, borderRadius:6, color:C.green, cursor:'pointer', fontSize:11, fontWeight:700 }}>
 Complete
 </button>
 )}
 {task.status !== 'Completed' && task.status !== 'Skipped' && (
 <button onClick={()=>handleUpdateStatus(task.id,'Skipped')}
 style={{ padding:'6px 10px', background:'rgba(96,104,128,.1)', border:'1px solid rgba(96,104,128,.3)', borderRadius:6, color:C.text3, cursor:'pointer', fontSize:11 }}>
 Skip
 </button>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 )}

 {/* ── ROOMS TAB ── */}
 {!loading && tab==='rooms' && (
 <div>
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
 {rooms.map(room => {
 const rs = ROOM_STATUS[room.status] || ROOM_STATUS['Vacant Clean'];
 const rt = roomTypes.find(t=>t.id===room.room_type_id);
 return (
 <div key={room.id} style={{ background:C.surface, border:`2px solid ${rs.color}33`, borderRadius:14, padding:16 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
 <div style={{ fontSize:22, fontWeight:900, color:C.text }}>#{room.room_number}</div>
 <span style={{ padding:'3px 8px', borderRadius:6, fontSize:11, fontWeight:700, background:`${rs.color}20`, color:rs.color }}>{rs.icon} {room.status}</span>
 </div>
 <div style={{ fontSize:12, color:C.text2, marginBottom:4 }}>{rt?.name||'Standard'}</div>
 <div style={{ fontSize:11, color:C.text3, marginBottom:12 }}>Floor {room.floor||1}</div>
 {/* Quick status update */}
 <select value={room.status} onChange={e=>handleUpdateRoom(room.id,e.target.value)}
 style={{ ...sel, padding:'6px 8px', fontSize:11 }}>
 {Object.keys(ROOM_STATUS).map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 {/* Quick task button */}
 {(room.status==='Vacant Dirty'||room.status==='Occupied') && (
 <button onClick={()=>{ setForm(p=>({...p,room_id:room.id,task_type:room.status==='Vacant Dirty'?'Cleaning':'Turndown'})); setShowAdd(true); }}
 style={{ marginTop:8, width:'100%', padding:'6px', background:`${C.accent}20`, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, cursor:'pointer', fontSize:11, fontWeight:600 }}>
 + Assign Task
 </button>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* ── SCHEDULE TAB ── */}
 {!loading && tab==='schedule' && (
 <div>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden' }}>
 <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, fontWeight:700, fontSize:15, color:C.text }}>
 Today's Schedule
 </div>
 {['High','Normal','Low'].map(priority => {
 const pts = tasks.filter(t=>t.priority===priority);
 if (pts.length===0) return null;
 return (
 <div key={priority}>
 <div style={{ padding:'8px 18px', background:`${PRIORITY_C[priority]}15`, fontSize:11, fontWeight:700, color:PRIORITY_C[priority], textTransform:'uppercase', letterSpacing:'.06em' }}>
 {priority} Priority ({pts.length})
 </div>
 {pts.map(task=>{
 const st = STATUS_C[task.status]||STATUS_C['Pending'];
 return (
 <div key={task.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 18px', borderBottom:`1px solid ${C.border}` }}>
 <div>
 <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{task.task_type} — {task.room_number?`Room #${task.room_number}`:'General'}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{task.assigned_to_name||'Unassigned'}</div>
 </div>
 <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, background:st.bg, color:st.color }}>{task.status}</span>
 </div>
 );
 })}
 </div>
 );
 })}
 {tasks.length===0 && (
 <div style={{ textAlign:'center', padding:40, color:C.text2 }}>No tasks scheduled for today</div>
 )}
 </div>
 </div>
 )}

 {/* ── ADD TASK MODAL ── */}
 {showAdd && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:24, maxWidth:480, width:'100%' }}>
 <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:16 }}> Add Housekeeping Task</div>
 <div style={{ display:'grid', gap:12 }}>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Room (Optional)</label>
 <select style={sel} value={form.room_id} onChange={e=>setForm(p=>({...p,room_id:e.target.value}))}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="">General / No specific room</option>
 {rooms.map(r=>{
 const rt = roomTypes.find(t=>t.id===r.room_type_id);
 return <option style={{background:'#0F2040',color:'#F0F2FF'}} key={r.id} value={r.id}>#{r.room_number} — {rt?.name||'Room'} ({r.status})</option>;
 })}
 </select>
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Task Type *</label>
 <select style={sel} value={form.task_type} onChange={e=>setForm(p=>({...p,task_type:e.target.value}))}>
 {TASK_TYPES.map(t=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={t}>{t}</option>)}
 </select>
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Priority</label>
 <select style={sel} value={form.priority} onChange={e=>setForm(p=>({...p,priority:e.target.value}))}>
 {PRIORITIES.map(p=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={p}>{p}</option>)}
 </select>
 </div>
 <div>
 <label style={{ display:'block', fontSize:12, color:C.text2, marginBottom:5, fontWeight:600 }}>Assign To</label>
 <select style={sel} value={form.assigned_to} onChange={e=>setForm(p=>({...p,assigned_to:e.target.value}))}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="">Unassigned</option>
 {users.map(u=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={u.id} value={u.id}>{u.name}</option>)}
 </select>
 </div>
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
 <button onClick={()=>{setShowAdd(false);setForm({room_id:'',task_type:'Cleaning',assigned_to:'',priority:'Normal',notes:''}); }}
 style={{ padding:'10px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleCreate} disabled={saving}
 style={{ padding:'10px 22px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, opacity:saving?.6:1 }}>
 {saving?'Creating…':'Create Task'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
